import { ArrowPathIcon, TagIcon, BanknotesIcon, ChatBubbleLeftEllipsisIcon } from '@heroicons/react/20/solid'
import { getOrderStatusColor } from '@/hooks/useOrderStatus'
import { getFinanceStageColor } from '@/hooks/useFinanceStatus'

interface StateChange {
  id: number
  /** ISO 8601 with TZ — used for timeAgo computation */
  changed_at: string | null
  /** Pre-formatted server-side date for tooltip/absolute display */
  changed_at_display?: string | null
  user_name: string | null
  from_state: string | null
  to_state: string | null
  // Backend splits sub_state into name/comment via SubState#sub_state_kind.
  // Legacy rows without a kind keep the generic 'sub_state' value.
  change_type: 'status' | 'sub_state' | 'sub_state_name' | 'sub_state_comment' | 'finance'
}

interface StatusLogTabProps {
  stateChanges: StateChange[]
}

function humanize(s: string | null) {
  if (!s) return '—'
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

// Buckets:
//   < 1 min                 →  Ns
//   1 min – 1 hour          →  Nm
//   1 hour – 24 hours       →  Nh Nm
//   1 day – 30 days         →  Nd Nh
//   30 days – 365 days      →  Nmo Nd
//   1+ year                 →  Ny Nmo
// Negative diffs (client clock < server clock) clamp to "just now"
// instead of leaking "-3596s ago".
export function timeAgo(dateStr: string | null, nowRef?: Date): string {
  if (!dateStr) return ''
  const date = new Date(dateStr)
  if (isNaN(date.getTime())) return ''
  const now = nowRef || new Date()
  let totalSeconds = Math.floor((now.getTime() - date.getTime()) / 1000)
  if (totalSeconds < 0) totalSeconds = 0

  if (totalSeconds < 60) {
    return `${totalSeconds}s ago`
  }

  const totalMinutes = Math.floor(totalSeconds / 60)
  if (totalMinutes < 60) {
    return `${totalMinutes}m ago`
  }

  const totalHours = Math.floor(totalMinutes / 60)
  if (totalHours < 24) {
    const m = totalMinutes - totalHours * 60
    return m > 0 ? `${totalHours}h ${m}m ago` : `${totalHours}h ago`
  }

  const totalDays = Math.floor(totalHours / 24)
  if (totalDays < 30) {
    const h = totalHours - totalDays * 24
    return h > 0 ? `${totalDays}d ${h}h ago` : `${totalDays}d ago`
  }

  // 30-day months — close enough for a human-readable "342d 8h" → "11mo 12d".
  if (totalDays < 365) {
    const months = Math.floor(totalDays / 30)
    const remainingDays = totalDays - months * 30
    return remainingDays > 0 ? `${months}mo ${remainingDays}d ago` : `${months}mo ago`
  }

  const years = Math.floor(totalDays / 365)
  const remainingDays = totalDays - years * 365
  const remainingMonths = Math.floor(remainingDays / 30)
  return remainingMonths > 0 ? `${years}y ${remainingMonths}mo ago` : `${years}y ago`
}

// ─── Color mapping ──────────────────────────────

const PILL_STYLES: Record<string, { dot: string; bg: string; ring: string }> = {
  sky:     { dot: 'fill-sky-500',     bg: 'bg-sky-50 dark:bg-sky-500/10',         ring: 'ring-sky-200 dark:ring-sky-500/20' },
  indigo:  { dot: 'fill-indigo-500',  bg: 'bg-indigo-50 dark:bg-indigo-500/10',   ring: 'ring-indigo-200 dark:ring-indigo-500/20' },
  violet:  { dot: 'fill-violet-500',  bg: 'bg-violet-50 dark:bg-violet-500/10',   ring: 'ring-violet-200 dark:ring-violet-500/20' },
  amber:   { dot: 'fill-amber-500',   bg: 'bg-amber-50 dark:bg-amber-500/10',     ring: 'ring-amber-200 dark:ring-amber-500/20' },
  orange:  { dot: 'fill-orange-500',  bg: 'bg-orange-50 dark:bg-orange-500/10',   ring: 'ring-orange-200 dark:ring-orange-500/20' },
  red:     { dot: 'fill-red-500',     bg: 'bg-red-50 dark:bg-red-500/10',         ring: 'ring-red-200 dark:ring-red-500/20' },
  emerald: { dot: 'fill-emerald-500', bg: 'bg-emerald-50 dark:bg-emerald-500/10', ring: 'ring-emerald-200 dark:ring-emerald-500/20' },
  lime:    { dot: 'fill-lime-500',    bg: 'bg-lime-50 dark:bg-lime-500/10',       ring: 'ring-lime-200 dark:ring-lime-500/20' },
  yellow:  { dot: 'fill-yellow-500',  bg: 'bg-yellow-50 dark:bg-yellow-500/10',   ring: 'ring-yellow-200 dark:ring-yellow-500/20' },
  cyan:    { dot: 'fill-cyan-500',    bg: 'bg-cyan-50 dark:bg-cyan-500/10',       ring: 'ring-cyan-200 dark:ring-cyan-500/20' },
  blue:    { dot: 'fill-blue-500',    bg: 'bg-blue-50 dark:bg-blue-500/10',       ring: 'ring-blue-200 dark:ring-blue-500/20' },
  teal:    { dot: 'fill-teal-500',    bg: 'bg-teal-50 dark:bg-teal-500/10',       ring: 'ring-teal-200 dark:ring-teal-500/20' },
  green:   { dot: 'fill-green-500',   bg: 'bg-green-50 dark:bg-green-500/10',     ring: 'ring-green-200 dark:ring-green-500/20' },
  purple:  { dot: 'fill-purple-500',  bg: 'bg-purple-50 dark:bg-purple-500/10',   ring: 'ring-purple-200 dark:ring-purple-500/20' },
  fuchsia: { dot: 'fill-fuchsia-500', bg: 'bg-fuchsia-50 dark:bg-fuchsia-500/10', ring: 'ring-fuchsia-200 dark:ring-fuchsia-500/20' },
  pink:    { dot: 'fill-pink-500',    bg: 'bg-pink-50 dark:bg-pink-500/10',       ring: 'ring-pink-200 dark:ring-pink-500/20' },
  rose:    { dot: 'fill-rose-500',    bg: 'bg-rose-50 dark:bg-rose-500/10',       ring: 'ring-rose-200 dark:ring-rose-500/20' },
  zinc:    { dot: 'fill-zinc-400',    bg: 'bg-zinc-50 dark:bg-zinc-500/10',       ring: 'ring-zinc-200 dark:ring-zinc-500/20' },
  stone:   { dot: 'fill-stone-500',   bg: 'bg-stone-50 dark:bg-stone-500/10',     ring: 'ring-stone-200 dark:ring-stone-500/20' },
}

const NODE_BG: Record<string, string> = {
  status:             'bg-indigo-100 dark:bg-indigo-500/20',
  sub_state:          'bg-zinc-100 dark:bg-zinc-700',
  sub_state_name:     'bg-zinc-100 dark:bg-zinc-700',
  sub_state_comment:  'bg-sky-100 dark:bg-sky-500/20',
  finance:            'bg-amber-100 dark:bg-amber-500/20',
}

const NODE_ICON_COLOR: Record<string, string> = {
  status:             'text-indigo-600 dark:text-indigo-400',
  sub_state:          'text-zinc-500 dark:text-zinc-400',
  sub_state_name:     'text-zinc-500 dark:text-zinc-400',
  sub_state_comment:  'text-sky-600 dark:text-sky-400',
  finance:            'text-amber-600 dark:text-amber-400',
}

function TypeIcon({ changeType }: { changeType: string }) {
  const cls = 'size-4'
  switch (changeType) {
    // ChatBubble icon for sub_state_comment so the timeline visually separates
    // metadata edits (Tag) from free-form annotation edits (ChatBubble).
    case 'sub_state_comment': return <ChatBubbleLeftEllipsisIcon className={cls} />
    case 'sub_state':
    case 'sub_state_name':    return <TagIcon className={cls} />
    case 'finance':           return <BanknotesIcon className={cls} />
    default:                  return <ArrowPathIcon className={cls} />
  }
}

function typeLabel(changeType: string) {
  switch (changeType) {
    case 'sub_state_name':    return 'changed sub state name'
    case 'sub_state_comment': return 'changed sub state comment'
    case 'sub_state':         return 'changed sub state'
    case 'finance':           return 'changed finance stage'
    default:                  return 'changed status'
  }
}

function StatusPill({ state, changeType }: { state: string | null; changeType: string }) {
  if (!state) return <span className="text-xs text-zinc-400">—</span>

  let style = PILL_STYLES.zinc
  if (changeType === 'finance') {
    const color = getFinanceStageColor(state)
    style = PILL_STYLES[color] || PILL_STYLES.zinc
  } else if (changeType === 'sub_state' || changeType === 'sub_state_name') {
    style = PILL_STYLES.zinc
  } else if (changeType === 'sub_state_comment') {
    style = PILL_STYLES.sky
  } else if (state === 'Approved') {
    style = PILL_STYLES.green
  } else if (state === 'Unapproved') {
    style = PILL_STYLES.red
  } else {
    const color = getOrderStatusColor(state) || 'zinc'
    style = PILL_STYLES[color] || PILL_STYLES.zinc
  }

  return (
    <span className={`inline-flex items-center gap-x-1.5 rounded-full px-2 py-0.5 text-xs font-medium text-gray-900 ring-1 dark:text-zinc-200 ${style.bg} ${style.ring}`}>
      <svg viewBox="0 0 6 6" aria-hidden="true" className={`size-1.5 ${style.dot}`}>
        <circle r={3} cx={3} cy={3} />
      </svg>
      {humanize(state)}
    </span>
  )
}

// ─── Component ──────────────────────────────────

export default function StatusLogTab({ stateChanges }: StatusLogTabProps) {
  return (
    <div data-testid="log-tab">
      {stateChanges.length === 0 ? (
        <p className="py-8 text-center text-sm text-zinc-400">No status changes recorded</p>
      ) : (
        <div className="flow-root">
          <ul role="list" className="-mb-8">
            {stateChanges.map((sc, idx) => (
              <li key={sc.id}>
                <div className="relative pb-8">
                  {idx !== stateChanges.length - 1 && (
                    <span aria-hidden="true" className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-zinc-700" />
                  )}
                  <div className="relative flex items-start space-x-3">
                    <div className="relative">
                      <div className={`flex size-8 items-center justify-center rounded-full ring-8 ring-white dark:ring-zinc-900 ${NODE_BG[sc.change_type] || NODE_BG.status}`}>
                        <span className={NODE_ICON_COLOR[sc.change_type] || NODE_ICON_COLOR.status}>
                          <TypeIcon changeType={sc.change_type} />
                        </span>
                      </div>
                    </div>
                    <div className="min-w-0 flex-1 py-0.5">
                      <div className="text-sm text-gray-500 dark:text-zinc-400">
                        <span className="font-medium text-gray-900 dark:text-white">{sc.user_name || 'System'}</span>
                        {' '}{typeLabel(sc.change_type)}{' '}
                        <span className="group relative inline-block cursor-default whitespace-nowrap text-xs text-gray-400 dark:text-zinc-500">
                          {timeAgo(sc.changed_at)}
                          {(sc.changed_at_display || sc.changed_at) && (
                            <span className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1.5 -translate-x-1/2 rounded-md bg-white px-2 py-1 text-[11px] font-medium whitespace-nowrap text-zinc-900 opacity-0 shadow-lg ring-1 ring-zinc-200 transition-opacity group-hover:opacity-100 dark:bg-zinc-800 dark:text-zinc-100 dark:ring-zinc-700">
                              {sc.changed_at_display || sc.changed_at}
                            </span>
                          )}
                        </span>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2">
                        <StatusPill state={sc.from_state} changeType={sc.change_type} />
                        <span className="text-xs text-zinc-400">→</span>
                        <StatusPill state={sc.to_state} changeType={sc.change_type} />
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
